export type LinkifyPart = { type: 'text'; value: string } | { type: 'link'; value: string; href: string };

const URL_PATTERN = /(?:https?:\/\/|www\.)[^\s<>"']+/gi;
const TRAILING_PUNCTUATION = /[.,;:!?'"\]}]$/;

const count = (value: string, char: string) => value.split(char).length - 1;

// Drop punctuation that ends the sentence rather than the URL, e.g. "see https://x.az/track." or "(https://x.az)".
const trimTrailing = (value: string) => {
  let result = value;
  while (result.length) {
    if (TRAILING_PUNCTUATION.test(result)) result = result.slice(0, -1);
    else if (result.endsWith(')') && count(result, ')') > count(result, '(')) result = result.slice(0, -1);
    else break;
  }
  return result;
};

const toSafeHref = (value: string): string | null => {
  try {
    const url = new URL(/^www\./i.test(value) ? `https://${value}` : value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.href : null;
  } catch {
    return null;
  }
};

// Splits plain text into text and http(s) link parts so links can be rendered as anchors without injecting HTML.
export const linkifyText = (text: string): LinkifyPart[] => {
  const parts: LinkifyPart[] = [];
  let lastIndex = 0;

  const pushText = (value: string) => {
    if (!value) return;
    const last = parts[parts.length - 1];
    if (last?.type === 'text') last.value += value;
    else parts.push({ type: 'text', value });
  };

  const source = text || '';
  // exec loop instead of matchAll: the web client targets ES5 without downlevelIteration.
  const pattern = new RegExp(URL_PATTERN.source, URL_PATTERN.flags);
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(source)) !== null) {
    const start = match.index;
    // Skip matches glued to a preceding word character, e.g. "foowww.bar".
    if (start > 0 && /\w/.test(source[start - 1])) continue;

    const value = trimTrailing(match[0]);
    const href = toSafeHref(value);
    if (!href) continue;

    pushText(source.slice(lastIndex, start));
    parts.push({ type: 'link', value, href });
    lastIndex = start + value.length;
  }

  pushText(source.slice(lastIndex));
  return parts;
};
